/*
 * Continue feed: an endless product grid fed from an ordered list of collections, skipping products already on
 * the page. Used by sections/product-keep-browsing.liquid (under Recently Viewed on the PDP); written to be reused
 * at the collection end (docs/theme/CONTINUITY.md #5). docs/theme/CONTINUITY.md#11-product-page-keep-browsing-feed
 *
 *   new ContinueFeed(root, options)       (also root.continueFeed, for tests and callers)
 *
 *   root                 holds [data-feed-sentinel] (loads when within 1.5 screens) and optionally a
 *                        [data-feed-more] "Show more" button
 *   options.sources      [{ url: '/collections/kaftan', heading: 'Keep browsing Kaftans' }, ...], loaded in order,
 *                        each until it runs out; an empty or failing one is skipped
 *   options.query        query string added to every request. The response must hold
 *                        [data-feed-page data-has-next="true|false"] with one .product child per card
 *                        (e.g. section_id=product-keep-browsing-cards&filter.v.availability=1)
 *   options.seen         Set of product IDs (strings) never to show; shared, and every card added goes into it.
 *                        Cards already in <main> are added before each load
 *   options.pending      CSS selector matching while a row above is still loading (Related Products' skeleton).
 *                        Each load waits for it, up to 3 s, so that row's cards count as shown
 *   options.perLoad      cards per load (8)
 *   options.autoRounds   loads on scroll before the "Show more" button takes over (4)
 *   options.target       (source) => element the source's cards are appended to. Default: a heading + grid
 *                        group cloned from root's <template data-feed-group> into [data-feed-groups]
 *   options.end          () => void, run once when every source has run out (never while "Show more" could still
 *                        load more, nor when the feed added nothing). Default: un-hide root's [data-feed-end]
 *   options.trackAs      Clarity prefix: events <trackAs>_load, <trackAs>_click and <trackAs>_chip (a
 *                        [data-feed-chip] link in root), tag <trackAs>_rounds
 */
(function () {
    if (window.ContinueFeed) return;

    // Upper bound on requests per load, for when most of a page is already shown (e.g. the first page of the
    // product's collection is also in Related Products). The next load picks up where this one stopped.
    const MAX_FETCHES_PER_LOAD = 3;
    // A row that never comes into view (the shopper jumped to the end) keeps its skeleton; stop waiting for it.
    const PENDING_WAIT_MS = 3000;

    class ContinueFeed {
        constructor(root, options) {
            this.root = root;
            this.options = Object.assign({ query: '', seen: new Set(), perLoad: 8, autoRounds: 4, trackAs: '' }, options);
            this.sources = this.options.sources.map((source) => Object.assign({ page: 0, done: false }, source));
            this.seen = this.options.seen;
            this.buffer = [];
            this.rounds = 0;
            this.busy = false;
            this.sentinel = root.querySelector('[data-feed-sentinel]');
            this.more = root.querySelector('[data-feed-more]');
            root.continueFeed = this;

            if (this.more) this.more.addEventListener('click', () => this.load());
            root.addEventListener('click', (event) => {
                if (event.target.closest('.product-item a')) this.track('event', 'click');
                else if (event.target.closest('[data-feed-chip]')) this.track('event', 'chip');
            });

            // An observer only reports changes, so update() re-observes after each load: a sentinel still in range
            // after a short load fires again.
            this.observer = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) this.load();
            }, { rootMargin: '0px 0px 150% 0px' });
            this.observer.observe(this.sentinel);
        }

        async load() {
            if (this.busy) return;
            this.busy = true;
            if (this.more) this.more.disabled = true;

            await this.settle();
            const batch = await this.collect();
            // Related Products and Recently Viewed load lazily and may have filled in while the requests were out.
            this.addSeenOnPage();
            const cards = batch.filter((item) => !this.seen.has(item.id));
            cards.forEach((item) => {
                this.seen.add(item.id);
                this.targetFor(item.source).appendChild(item.card);
            });
            if (cards.length) {
                this.rounds++;
                this.initCards(this.root);
                this.track('event', 'load');
                this.track('set', 'rounds', String(this.rounds));
            }

            this.busy = false;
            if (this.more) this.more.disabled = false;
            this.update();
        }

        // Up to perLoad unseen cards, from the buffer first, then from the next pages of the sources in order.
        async collect() {
            this.addSeenOnPage();
            const batch = [];
            const taken = new Set();
            let fetches = 0;
            while (batch.length < this.options.perLoad) {
                const item = this.buffer.shift();
                if (item) {
                    if (!this.seen.has(item.id) && !taken.has(item.id)) {
                        taken.add(item.id);
                        batch.push(item);
                    }
                    continue;
                }
                const source = this.sources.find((candidate) => !candidate.done);
                if (!source || fetches === MAX_FETCHES_PER_LOAD) break;
                fetches++;
                this.buffer.push(...await this.fetchPage(source));
            }
            return batch;
        }

        async fetchPage(source) {
            source.page++;
            const url = new URL(source.url, window.location.origin);
            new URLSearchParams(this.options.query).forEach((value, key) => url.searchParams.append(key, value));
            url.searchParams.set('page', source.page);

            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`${response.status} ${url}`);
                const page = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-feed-page]');
                if (!page || page.dataset.hasNext !== 'true') source.done = true;
                if (!page) return [];
                return [...page.children]
                    .map((card) => ({ card, source, id: card.querySelector('.product-item[data-product-id]')?.dataset.productId }))
                    .filter((item) => item.id);
            } catch (error) {
                // A failing source is skipped; the feed carries on with the next one.
                source.done = true;
                console.error(error);
                return [];
            }
        }

        async settle() {
            const started = Date.now();
            while (this.options.pending && document.querySelector(this.options.pending) && Date.now() - started < PENDING_WAIT_MS) {
                await new Promise((resolve) => setTimeout(resolve, 200));
            }
        }

        // Every card on the page (Related Products, Recently Viewed, Complete the look, this feed) counts as shown.
        addSeenOnPage() {
            document.querySelectorAll('main .product-item[data-product-id]').forEach((item) => this.seen.add(item.dataset.productId));
        }

        update() {
            const exhausted = !this.buffer.some((item) => !this.seen.has(item.id)) && this.sources.every((source) => source.done);
            if (exhausted || this.rounds >= this.options.autoRounds) this.observer.disconnect();
            else {
                this.observer.unobserve(this.sentinel);
                this.observer.observe(this.sentinel);
            }
            if (this.more) this.more.hidden = exhausted || this.rounds < this.options.autoRounds;
            if (exhausted && this.rounds && !this.ended) {
                this.ended = true;
                this.end();
            }
        }

        end() {
            if (this.options.end) return this.options.end();
            const end = this.root.querySelector('[data-feed-end]');
            if (end) end.hidden = false;
        }

        targetFor(source) {
            if (!source.list) source.list = this.options.target ? this.options.target(source) : this.createGroup(source);
            return source.list;
        }

        createGroup(source) {
            const group = this.root.querySelector('template[data-feed-group]').content.firstElementChild.cloneNode(true);
            const heading = group.querySelector('[data-feed-heading]');
            if (source.heading) heading.textContent = source.heading;
            else heading.remove();
            this.root.querySelector('[data-feed-groups]').appendChild(group);
            return group.querySelector('[data-feed-list]');
        }

        // What theme.js loaderProductBlock does once its cards are in (wishlist hearts, hover video, currency,
        // custom cursor, card countdown), through the functions theme.js shares on window.sharedFunctions.
        initCards(root) {
            const shared = window.sharedFunctions || {};
            if (window.wishlist && window.wishlist.show && shared.setLocalStorageProductForWishlist) shared.setLocalStorageProductForWishlist();
            if (shared.swapHoverVideoProductCard) shared.swapHoverVideoProductCard();
            if (shared.checkNeedToConvertCurrency && shared.checkNeedToConvertCurrency() && window.Currency && window.jQuery) {
                Currency.convertAll(window.shop_currency, jQuery('#currencies .active').attr('data-currency'), 'span.money', 'money_format');
            }
            if (document.body.classList.contains('cursor-fixed__show') && window.sharedFunctionsAnimation) {
                window.sharedFunctionsAnimation.onEnterButton();
                window.sharedFunctionsAnimation.onLeaveButton();
            }
            // Starts a timer per call, so only when the new cards have a countdown.
            if (shared.productCountdownCard && root.querySelector('.card-countDown [data-countdown-id]')) shared.productCountdownCard();
        }

        track(kind, name, value) {
            if (!this.options.trackAs || typeof window.clarity !== 'function') return;
            const key = `${this.options.trackAs}_${name}`;
            if (kind === 'set') window.clarity('set', key, value);
            else window.clarity('event', key);
        }
    }

    window.ContinueFeed = ContinueFeed;

    // Feeds configured in markup: <div data-continue-feed><script type="application/json" data-feed-config>.
    const init = (scope) => {
        scope.querySelectorAll('[data-continue-feed]:not([data-feed-ready])').forEach((root) => {
            const config = root.querySelector('script[data-feed-config]');
            if (!config) return;
            root.setAttribute('data-feed-ready', '');
            const options = JSON.parse(config.textContent);
            options.seen = new Set((options.seen || []).map(String));
            new ContinueFeed(root, options);
        });
    };

    init(document);
    // Theme editor: a re-rendered section's <script> does not run again.
    document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
